import React from 'react';
import {
  computeMatchContext,
  renderHighlightedValue,
} from '../../../shared/utils/matchContext';
import './SiteScopedToolList.css';

/* Column SET mirrors S1 AssetToolList.jsx (COLUMNS, same order/headers) —
   column parity across canonical-search surfaces. This is a parallel small
   table over raw snake_case search_inventory / get_tools payloads (which
   cannot render through DisposalToolCard — Phase 1 Summary surprise #2);
   only the column SET is mirrored, not the S1 component. */
const COLUMNS = [
  { key: 'supplier_tool_id', header: 'Tool ID' },
  { key: 'alt_tool_id', header: 'Alt Tool ID' },
  { key: 'tool_type', header: 'Tool Type' },
  { key: 'material', header: 'Material' },
  { key: 'oem', header: 'OEM' },
  { key: 'site_location', header: 'Site Location' },
  { key: 'detail_location', header: 'Detail Location' },
  { key: 'preliminary_tool_status', header: 'Prelim Status' },
];

/**
 * Site-scoped tool list for the Disposal Portal site-picker path.
 *
 * @param {object[]} tools            Raw snake_case tool docs to display
 *                                    (search_inventory results when searching,
 *                                    else all site tools from get_tools).
 * @param {boolean}  loading          Initial site-tools fetch in flight.
 * @param {?string}  error            Fetch error message, or null.
 * @param {string}   searchQuery      Current search box text.
 * @param {boolean}  isSearching      query length >= MIN_QUERY_LENGTH
 *                                    (single gate, computed by parent).
 * @param {string}   siteName         Display label for the selected site.
 * @param {Set}      disposalToolIds  raw tool_id of tools on a disposal
 *                                    project at this site (D7 membership).
 * @param {(tool)=>void} onRowClick   Invoked only for disposal-project rows.
 */
export default function SiteScopedToolList({
  tools,
  loading,
  error,
  searchQuery,
  isSearching,
  siteName,
  disposalToolIds,
  onRowClick,
}) {
  if (loading) {
    return (
      <div className="sstl-empty">
        <div className="sstl-spinner" />
      </div>
    );
  }

  if (error) {
    return <div className="sstl-empty sstl-error">{error}</div>;
  }

  const list = Array.isArray(tools) ? tools : [];

  if (list.length === 0) {
    return (
      <div className="sstl-empty">
        {isSearching
          ? `No tools at ${siteName} match "${searchQuery}"`
          : `No tools at ${siteName}`}
      </div>
    );
  }

  return (
    <div className="sstl-wrap">
      <div className="sstl-status">
        {isSearching
          ? `${list.length} result${list.length === 1 ? '' : 's'} for "${searchQuery}" at ${siteName}`
          : `Showing all tools at ${siteName}`}
      </div>

      <div className="sstl-table-wrap">
        <table className="sstl-table">
          <thead>
            <tr>
              {COLUMNS.map(col => (
                <th key={col.key}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map(tool => {
              const rid = tool.tool_id || tool.id;
              const onProject = disposalToolIds && disposalToolIds.has(rid);
              const matchCtx = isSearching
                ? computeMatchContext(tool, searchQuery)
                : null;

              return (
                <tr
                  key={rid}
                  className={`sstl-row ${onProject ? 'sstl-row-clickable' : 'sstl-row-disabled'}`}
                  onClick={onProject ? () => onRowClick(tool) : undefined}
                  title={onProject ? undefined : 'Not on a disposal project'}
                >
                  <td>
                    <div className="sstl-cell-main">
                      {isSearching && tool.supplier_tool_id
                        ? renderHighlightedValue(tool.supplier_tool_id, searchQuery)
                        : (tool.supplier_tool_id || '—')}
                    </div>
                    {matchCtx && (
                      <div className="sstl-match-context">
                        Matched: {matchCtx.label} ·{' '}
                        {renderHighlightedValue(matchCtx.value, searchQuery)}
                      </div>
                    )}
                  </td>
                  <td>{tool.alt_tool_id || '—'}</td>
                  <td>{tool.tool_type || '—'}</td>
                  <td>{tool.material || '—'}</td>
                  <td>{tool.oem || '—'}</td>
                  <td>{tool.site_location || '—'}</td>
                  <td>{tool.detail_location || '—'}</td>
                  <td>
                    <span className="sstl-status-badge">
                      {(tool.preliminary_tool_status || 'TBD').toUpperCase()}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
